/*
 * Promo pricing and installment text
 *
 * Keeps the promo price and the installment amount on product forms in sync with
 * the price the theme shows, for example when a different variant is selected.
 * See snippets/promo-price.liquid and snippets/product-price.liquid
 */

(function () {
  const settings = (window.theme && window.theme.pricing) || {};
  const promoPercent = settings.promoPercent || 0;

  const selectors = {
    block: '.product__block',
    wrapper: '[data-price-wrapper]',
    productPrice: '[data-product-price]',
    promoPrice: '[data-promo-price-new]',
    installment: '[data-installment-price]',
    installmentAmount: '[data-installment-amount]',
    shop: '[data-installment-shop]',
    shopLogo: '[data-installment-shop-logo]',
    shopHelp: '[data-installment-shop-help]',
    paymentTerms: 'shopify-payment-terms',
  };

  const attributes = {
    promoWrapper: 'data-promo-price-wrapper',
    installment: 'data-installment-price',
  };

  const classes = {
    active: 'promo-price-active',
    hidden: 'hidden',
  };

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const moneyFormat = window.theme.moneyFormat || '${{amount}}';
  const formatMatch = moneyFormat.match(placeholderRegex);
  const formatType = formatMatch ? formatMatch[1] : 'amount';

  function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
    number = (number / 100.0).toFixed(precision);

    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  function formatMoney(cents) {
    let value = '';

    switch (formatType) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', '');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return moneyFormat.replace(placeholderRegex, value);
  }

  /**
   * Reads the price in cents from a formatted price, e.g. "$1,145.00" returns 114500
   */
  function getCents(text) {
    const digits = text.replace(/\D/g, '');

    if (digits === '') return 0;

    const amount = parseInt(digits, 10);

    return formatType.includes('no_decimals') ? amount * 100 : amount;
  }

  /**
   * Updates the promo price and returns the price the customer pays in cents
   * Free or unavailable variants show the regular theme price
   */
  function updatePromoPrice(wrapper, price) {
    const promoPrice = wrapper.querySelector(selectors.promoPrice);

    if (!promoPercent || !promoPrice || !wrapper.hasAttribute(attributes.promoWrapper)) return price;

    if (!price) {
      wrapper.classList.remove(classes.active);
      return price;
    }

    const newPrice = Math.round((price * (100 - promoPercent)) / 100);

    promoPrice.innerHTML = formatMoney(newPrice);
    wrapper.classList.add(classes.active);

    return newPrice;
  }

  function updateInstallment(wrapper, price) {
    const block = wrapper.closest(selectors.block);
    const installment = block ? block.querySelector(selectors.installment) : null;
    const installmentAmount = installment ? installment.querySelector(selectors.installmentAmount) : null;

    if (!installmentAmount) return;

    const count = parseInt(installment.getAttribute(attributes.installment), 10) || 4;

    installment.classList.toggle(classes.hidden, !price);

    if (price) {
      installmentAmount.innerHTML = formatMoney(Math.round(price / count));
    }
  }

  function updatePrices(wrapper) {
    const productPrice = wrapper.querySelector(selectors.productPrice);

    if (!productPrice) return;

    const price = getCents(productPrice.textContent);
    const finalPrice = updatePromoPrice(wrapper, price);

    updateInstallment(wrapper, finalPrice);
  }

  /**
   * Shop Pay logo and help icon come from the Shop Pay terms that Shopify renders,
   * they only show when the product is eligible for Shop Pay Installments
   */
  function getShopPayButton(paymentTerms) {
    return paymentTerms && paymentTerms.shadowRoot ? paymentTerms.shadowRoot.querySelector('button') : null;
  }

  function updateShopPay(paymentTerms) {
    const block = paymentTerms.closest(selectors.block);
    const shop = block ? block.querySelector(selectors.shop) : null;

    if (!shop) return;

    const button = getShopPayButton(paymentTerms);
    const icons = button ? Array.from(paymentTerms.shadowRoot.querySelectorAll('svg')) : [];
    const logo = icons.find((icon) => !button.contains(icon));
    const helpIcon = icons.find((icon) => button.contains(icon));

    if (!logo) {
      shop.classList.add(classes.hidden);
      return;
    }

    const shopLogo = shop.querySelector(selectors.shopLogo);
    const shopHelp = shop.querySelector(selectors.shopHelp);

    shopLogo.replaceChildren(logo.cloneNode(true));

    if (helpIcon) {
      shopHelp.replaceChildren(helpIcon.cloneNode(true));
    } else {
      shopHelp.textContent = '?';
    }

    shopHelp.setAttribute('aria-label', button.getAttribute('aria-label') || 'Shop Pay');
    shop.classList.remove(classes.hidden);
  }

  const observedPaymentTerms = new WeakSet();

  function observePaymentTerms(paymentTerms, attempt = 0) {
    if (observedPaymentTerms.has(paymentTerms)) return;

    // Shopify renders the terms after the page loads
    if (!paymentTerms.shadowRoot) {
      if (attempt < 20) {
        setTimeout(() => observePaymentTerms(paymentTerms, attempt + 1), 500);
      }
      return;
    }

    observedPaymentTerms.add(paymentTerms);

    new MutationObserver(() => updateShopPay(paymentTerms)).observe(paymentTerms.shadowRoot, {childList: true, subtree: true});

    updateShopPay(paymentTerms);
  }

  function initPaymentTerms(container) {
    if (container.matches(selectors.paymentTerms)) {
      observePaymentTerms(container);
    }

    container.querySelectorAll(selectors.paymentTerms).forEach((paymentTerms) => observePaymentTerms(paymentTerms));
  }

  document.addEventListener('click', (event) => {
    const shopHelp = event.target.closest(selectors.shopHelp);

    if (!shopHelp) return;

    const block = shopHelp.closest(selectors.block);
    const button = getShopPayButton(block ? block.querySelector(selectors.paymentTerms) : null);

    if (button) {
      button.click();
    }
  });

  const observer = new MutationObserver((mutations) => {
    const wrappers = new Set();

    mutations.forEach((mutation) => {
      const target = mutation.target.nodeType === Node.ELEMENT_NODE ? mutation.target : mutation.target.parentElement;
      const productPrice = target ? target.closest(selectors.productPrice) : null;
      const wrapper = productPrice ? productPrice.closest(selectors.wrapper) : null;

      if (wrapper) {
        wrappers.add(wrapper);
      }

      // Product forms added later, for example Quick add
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          initPaymentTerms(node);
        }
      });
    });

    wrappers.forEach((wrapper) => updatePrices(wrapper));
  });

  observer.observe(document.body, {childList: true, characterData: true, subtree: true});

  initPaymentTerms(document.body);
})();
